import React, { useState } from 'react';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import { ArrowLeft, Calendar, Clock, Building2, MapPin, DollarSign, Users, TrendingUp, FileText, Briefcase, Target, MessageSquare, Brain, CheckCircle, AlertCircle, ExternalLink, Plus, X, Loader } from 'lucide-react';

const PracticeTimeModal = ({ 
interview,
suggestPracticeTimes,
handleConfirmMockSchedule,
handleRescheduleMockInterview,
setShowPracticeTimeModal,
isReschedule,
setIsReschedule
}) => {
const [showCustomTime, setShowCustomTime] = useState(false);
const [customDate, setCustomDate] = useState('');
const [customTime, setCustomTime] = useState('');
const [customErrors, setCustomErrors] = useState({});

const validateCustomTime = () => {
const errors = {};

if (!customDate) {
errors.date = 'Please select a date';
} else {
const selectedDate = new Date(customDate);
const today = new Date();
today.setHours(0, 0, 0, 0);

// Check if date is in the past
if (selectedDate < today) {
errors.date = 'Please select a future date';
}

// Check if date is after the real interview
if (interview?.date) {
const interviewDate = new Date(interview.date);
interviewDate.setHours(0, 0, 0, 0);

if (selectedDate >= interviewDate) {
errors.date = 'Practice session must be before the real interview';
}
}
}

if (!customTime) {
errors.time = 'Please select a time';
} else {
// Validate time format (HH:MM)
const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
if (!timeRegex.test(customTime)) {
errors.time = 'Invalid time format';
}
}

setCustomErrors(errors);
return Object.keys(errors).length === 0;
};

const handleCustomSubmit = () => {
if (validateCustomTime()) {
const customSuggestion = {
date: customDate,
time: customTime,
label: 'Custom Time'
};

if(isReschedule){
handleRescheduleMockInterview(customSuggestion)
}else{
handleConfirmMockSchedule(customSuggestion);
}
}
};

const getMinDate = () => {
const today = new Date();
return today.toISOString().split('T')[0];
};

const getMaxDate = () => {
if (!interview?.date) return null;
const interviewDate = new Date(interview.date);
interviewDate.setDate(interviewDate.getDate() - 1);
return interviewDate.toISOString().split('T')[0];
};

return (
<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-md">
<div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
<div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
<h2 className="text-lg font-bold text-gray-900">
{isReschedule ? 'Re-schedule Your Practice Session' : 'Select Practice Time'}
</h2>
<p className="text-gray-600 text-sm">
Choose {isReschedule ? 'another' : 'a'} time to practice for your interview with <span className="font-bold text-gray-900">{interview?.company_name || interview?.company}</span>
</p>
</div>

<div className="p-6 space-y-4">
{/* Suggested Times */}
<div className="space-y-3">
<h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
Suggested Times
</h3>
{suggestPracticeTimes().map((suggestion, index) => (
<button
key={index}
onClick={ isReschedule ? ()=>handleRescheduleMockInterview(suggestion)  : () => handleConfirmMockSchedule(suggestion) }
className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all text-left group"
>
<div className="flex items-center justify-between">
<div className="flex items-center gap-3">
<div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors">
<Calendar className="w-6 h-6 text-purple-600" />
</div>
<div>
<p className="font-semibold text-gray-900">{suggestion.label}</p>
<p className="text-sm text-gray-600">
{new Date(suggestion.date).toLocaleDateString('en-US', { 
weekday: 'long', 
month: 'long', 
day: 'numeric' 
})} at {suggestion.time}
</p>
</div>
</div>
<div className="text-purple-600">
<ArrowLeft className="w-5 h-5 rotate-180" />
</div>
</div>
</button>
))}
</div>

{/* Custom Time Section */}
<div className="pt-4 border-t border-gray-200">
{!showCustomTime ? (
<button
onClick={() => setShowCustomTime(true)}
className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-all text-left group"
>
<div className="flex items-center justify-between">
<div className="flex items-center gap-3">
<div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-purple-100 transition-colors">
<Clock className="w-6 h-6 text-gray-600 group-hover:text-purple-600" />
</div>
<div>
<p className="font-semibold text-gray-900">Custom Time</p>
<p className="text-sm text-gray-600">Choose your own date and time</p>
</div>
</div>
<div className="text-gray-400 group-hover:text-purple-600">
<Plus className="w-5 h-5" />
</div>
</div>
</button>
) : (
<div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
<div className="flex items-center justify-between mb-3">
<h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
Custom Time
</h3>
<button
onClick={() => {
setShowCustomTime(false);
setCustomDate('');
setCustomTime('');
setCustomErrors({});
}}
className="text-gray-400 hover:text-gray-600 transition-colors"
>
<X className="w-5 h-5" />
</button>
</div>

<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
{/* Date Input */}
<div>
<label htmlFor="custom-date" className="block text-sm font-medium text-gray-700 mb-1">
Date
</label>
<input
id="custom-date"
type="date"
value={customDate}
onChange={(e) => {
setCustomDate(e.target.value);
setCustomErrors({ ...customErrors, date: null });
}}
min={getMinDate()}
max={getMaxDate()}
className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
customErrors.date ? 'border-red-500' : 'border-gray-300'
}`}
/>
{customErrors.date && (
<p className="mt-1 text-sm text-red-600 flex items-center gap-1">
<AlertCircle className="w-4 h-4" />
{customErrors.date}
</p>
)}
</div>

{/* Time Input */}
<div>
<label htmlFor="custom-time" className="block text-sm font-medium text-gray-700 mb-1">
Time
</label>
<input
id="custom-time"
type="time"
value={customTime}
onChange={(e) => {
setCustomTime(e.target.value);
setCustomErrors({ ...customErrors, time: null });
}}
className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
customErrors.time ? 'border-red-500' : 'border-gray-300'
}`}
/>
{customErrors.time && (
<p className="mt-1 text-sm text-red-600 flex items-center gap-1">
<AlertCircle className="w-4 h-4" />
{customErrors.time}
</p>
)}
</div>
</div>

{/* Info Box */}
<div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
<AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
<p className="text-sm text-blue-800">
Practice session must be scheduled before your actual interview on{' '}
<span className="font-semibold">
{interview?.date && new Date(interview.date).toLocaleDateString('en-US', {
month: 'long',
day: 'numeric',
year: 'numeric'
})}
</span>
</p>
</div>

{/* Submit Custom Time */}
<button
onClick={handleCustomSubmit}
disabled={!customDate || !customTime}
className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
>
<CheckCircle className="w-5 h-5" />
Confirm Custom Time
</button>
</div>
)}
</div>
</div>

{/* Footer */}
<div className="p-6 border-t border-gray-200 bg-gray-50">
<button
onClick={() => {
setShowPracticeTimeModal(false);
setShowCustomTime(false);
setCustomDate('');
setCustomTime('');
setCustomErrors({});
setIsReschedule(false)
}}
className="w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors font-medium"
>
Cancel
</button>
</div>
</div>
</div>
);
};

export default PracticeTimeModal;